import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UrlFallback } from './urlFallback';

/** Префикс ключа lock-флага пинга; реальный ключ — с суффиксом пула, см. getPingLockStorageKey. */
const PING_REQUEST_LOCK_KEY = 'IsPingRequestAlive';
/** Префикс имени origin-wide Web Lock пинга; полное имя — с суффиксом пула, см. getPingWebLockName. */
const PING_WEB_LOCK_PREFIX = 'sp.pingRequest';
const DEBUG_PING_REQUEST_KEY = 'debugPingRequest';

/**
 * Ключ lock-флага пинга строится с суффиксом пула, чтобы запросы к одному пулу (напр. payment)
 * не ждали пинг другого (api) — они друг другу не мешают, и у каждого пула свой lock.
 */
export function getPingLockStorageKey(poolKey: string): string {
  return `${PING_REQUEST_LOCK_KEY}.${poolKey}`;
}

/**
 * Всё, что нужно gate-логике пинга от пула: ключ пула (для lock) и наличие пригодного
 * результата. Реализуют и UrlFallback, и UrlFallbackView.
 */
interface PingPoolIdentity {
  getStorageKeySuffix(): string;
  hasUsablePingSnapshot(): boolean;
}

function resolvePoolKey(pool: PingPoolIdentity): string {
  return pool.getStorageKeySuffix();
}

// таймаут запроса на пинг
const DEFAULT_CONTROL_REQUEST_TIMEOUT = 30000;
// интервал запроса на пинг
const DEFAULT_CONTROL_REQUEST_INTERVAL = 1800000;
// время протухания блокировки запроса на пинг
const DEFAULT_LOCK_TTL_MS = 40000;
// начальное окно ожидания публикации результата (grace), если пригодного snapshot ещё нет
const DEFAULT_PING_INITIAL_GRACE_MS = 1500;

export const pingRequestBusy$ = new BehaviorSubject<boolean>(false);
export const requestFallbackPreloaderMessage$ = new BehaviorSubject<
  string | null
>(null);

export function showRequestFallbackPreloader(message: string): void {
  requestFallbackPreloaderMessage$.next(message);
}

export function hideRequestFallbackPreloader(): void {
  requestFallbackPreloaderMessage$.next(null);
}

interface NavigatorWithConnection extends Navigator {
  connection?: EventTarget;
  mozConnection?: EventTarget;
  webkitConnection?: EventTarget;
}

/** TODO очищает старые данные, после релиза удалить */
const LEGACY_PING_SUCCESS_KEY = 'IsPingRequestReturnedSuccess';

/** TODO очищает старые данные, после релиза удалить */
function cleanupLegacyPingSuccessKey(poolKey: string): void {
  try {
    localStorage.removeItem(`${LEGACY_PING_SUCCESS_KEY}.${poolKey}`);
  } catch {
    // localStorage недоступен — не критично
  }
}

export function startPingRequestBackground(urlFallback: UrlFallback): void {
  cleanupLegacyPingSuccessKey(resolvePoolKey(urlFallback));
  const interval =
    environment.controlRequestInterval ?? DEFAULT_CONTROL_REQUEST_INTERVAL;
  const run = () => void pingRequest(urlFallback);
  const connection = getNetworkConnection();

  run();
  window.setInterval(run, interval);
  window.addEventListener('online', run);
  window.addEventListener('offline', run);
  connection?.addEventListener('change', run);
}

/**
 * Значение lock-а — "<startedAt>:<expiresAt>": expiresAt для проверки живости, startedAt для
 * расчёта grace (общего дедлайна попытки пинга, а не персонального таймера потребителя).
 * Поддерживаем и старый формат (одно число expiresAt) для совместимости при выкатке.
 */
function buildLockValue(startedAt: number, expiresAt: number): string {
  return `${startedAt}:${expiresAt}`;
}

function parseLockValue(
  raw: string | null,
): { startedAt: number; expiresAt: number } | null {
  if (!raw) {
    return null;
  }
  const parts = raw.split(':');
  if (parts.length >= 2) {
    const startedAt = Number(parts[0]);
    const expiresAt = Number(parts[1]);
    return Number.isFinite(startedAt) && Number.isFinite(expiresAt)
      ? { startedAt, expiresAt }
      : null;
  }
  const expiresAt = Number(raw);
  if (!Number.isFinite(expiresAt)) {
    return null;
  }
  const ttl = environment.controlRequestLockTTLMs ?? DEFAULT_LOCK_TTL_MS;
  return { startedAt: expiresAt - ttl, expiresAt };
}

function readLock(
  pool: PingPoolIdentity,
): { startedAt: number; expiresAt: number } | null {
  try {
    return parseLockValue(
      localStorage.getItem(getPingLockStorageKey(resolvePoolKey(pool))),
    );
  } catch {
    return null;
  }
}

export function isPingRequestAlive(pool: PingPoolIdentity): boolean {
  const lock = readLock(pool);
  return !!lock && lock.expiresAt > Date.now();
}

/**
 * В пределах ли начального grace-окна текущий (общий) ping. Grace отсчитывается от startedAt
 * lock-а, поэтому один и тот же дедлайн виден всем потребителям пула и не рестартится на
 * каждом запросе/итерации ожидания.
 */
export function isWithinInitialGrace(pool: PingPoolIdentity): boolean {
  const lock = readLock(pool);
  const grace =
    environment.controlRequestGraceMs ?? DEFAULT_PING_INITIAL_GRACE_MS;
  return !!lock && Date.now() < lock.startedAt + grace;
}

/**
 * Ждать публикацию свежего ping имеет смысл только пока: пригодного snapshot ещё нет, ping
 * реально идёт и не истёк grace. Во всех прочих случаях (есть результат / ping не активен /
 * grace вышел) работаем сразу — чтобы не держать запрос и плашку весь controlRequestLockTTLMs.
 */
export function shouldWaitForPingRequest(pool: PingPoolIdentity): boolean {
  return (
    !pool.hasUsablePingSnapshot() &&
    isPingRequestAlive(pool) &&
    isWithinInitialGrace(pool)
  );
}

/** Имя origin-wide Web Lock для пула — отдельное пространство имён от localStorage-ключей. */
function getPingWebLockName(poolKey: string): string {
  return `${PING_WEB_LOCK_PREFIX}.${poolKey}`;
}

/** Минимальный структурный тип Web Locks API — не зависим от наличия LockManager в lib.dom. */
interface WebLockManagerLike {
  request(
    name: string,
    options: { ifAvailable?: boolean },
    callback: (lock: unknown | null) => unknown,
  ): Promise<unknown>;
}

function getWebLockManager(): WebLockManagerLike | null {
  try {
    const locks = (navigator as unknown as { locks?: WebLockManagerLike })
      .locks;
    return locks && typeof locks.request === 'function' ? locks : null;
  } catch {
    return null;
  }
}

export function pingRequest(urlFallback: UrlFallback): Promise<void> {
  const poolKey = resolvePoolKey(urlFallback);
  const webLocks = getWebLockManager();
  if (!webLocks) {
    // Нет Web Locks API — прежняя advisory-дедупликация по localStorage-lock.
    return runPingRequestWithStorageDedup(urlFallback, poolKey);
  }
  // Web Locks: origin-wide эксклюзив на пул для всех вкладок/iframe. ifAvailable=true — не
  // ждём очередь, а сразу пропускаем, если ping уже идёт в другом контексте. Браузер сам
  // освобождает lock при уничтожении документа, поэтому осиротевшего lock-а, блокирующего
  // следующий ping, здесь возникнуть не может (в отличие от localStorage-lock).
  return webLocks
    .request(getPingWebLockName(poolKey), { ifAvailable: true }, (lock) => {
      if (!lock) {
        logPingRequest('skip-active-lock', {});
        return undefined;
      }
      return runPingCycle(urlFallback, poolKey);
    })
    .then(
      () => undefined,
      (error) => {
        // Сбой самого Web Locks API (а не отказ ping) не должен молча терять запуск ping:
        // откатываемся на localStorage-дедупликацию. runPingCycle не реджектится, поэтому в
        // эту ветку попадает только ошибка получения lock — двойного запуска ping не будет.
        logPingRequest('web-lock-error', { error: String(error) });
        return runPingRequestWithStorageDedup(urlFallback, poolKey);
      },
    );
}

function runPingRequestWithStorageDedup(
  urlFallback: UrlFallback,
  poolKey: string,
): Promise<void> {
  if (isPingRequestAlive(urlFallback)) {
    logPingRequest('skip-active-lock', {});
    return Promise.resolve();
  }
  return runPingCycle(urlFallback, poolKey);
}

function runPingCycle(
  urlFallback: UrlFallback,
  poolKey: string,
): Promise<void> {
  // localStorage-lock пишем всегда: он несёт startedAt/expiresAt для grace, видимого другим
  // инстансам (состояние Web Lock другим контекстам как данные недоступно).
  if (!acquirePingLock(poolKey)) {
    logPingRequest('skip-lock-failed', {});
    return Promise.resolve();
  }
  const urls = urlFallback.getUrls();
  const startedAt = Date.now();
  logPingRequest('start', {
    urls,
    urlCount: urls.length,
  });
  let successIndex = 0;
  let errorIndex = urls.length - 1;
  const nextSuccessResponseNo = () => successIndex++;
  const nextErrorResponseNo = () => errorIndex--;
  // По завершении всех XHR successIndex === числу успешных ответов — его и финализируем.
  const finish = () =>
    finishPingRequest(urlFallback, poolKey, successIndex, startedAt);
  return Promise.all(
    urls.map((url) =>
      pingUrl(url, nextSuccessResponseNo, nextErrorResponseNo, urlFallback),
    ),
  ).then(finish, finish);
}

function acquirePingLock(poolKey: string): boolean {
  try {
    const now = Date.now();
    const timeout = environment.controlRequestLockTTLMs ?? DEFAULT_LOCK_TTL_MS;
    localStorage.setItem(
      getPingLockStorageKey(poolKey),
      buildLockValue(now, now + timeout),
    );
    return true;
  } catch {
    return false;
  }
}

function finishPingRequest(
  urlFallback: UrlFallback,
  poolKey: string,
  successfulCount: number,
  startedAt?: number,
): void {
  try {
    localStorage.removeItem(getPingLockStorageKey(poolKey));
  } catch {
    // localStorage недоступен (private mode, quota и т.п.) — lock протухнет по TTL сам.
  }
  // Финализируем snapshot: авторитетное число успешных (в т.ч. 0, если все хосты упали) и
  // время завершения. Успех больше не хранится отдельным флагом — он часть snapshot.
  urlFallback.markPingCompleted(successfulCount);
  pingRequestBusy$.next(false);
  logPingRequest('finish', {
    elapsedMs: startedAt ? Date.now() - startedAt : null,
    successfulCount,
  });
}

function pingUrl(
  url: string,
  responseOrder: () => number,
  errorResponseOrder: () => number,
  urlFallback: UrlFallback,
): Promise<void> {
  const baseUrl = normalizeBaseUrl(url);
  const fullUrl = joinBaseAndPath(baseUrl, urlFallback.getPingRequestAddress());
  const timeout =
    environment.controlRequestTimeout ?? DEFAULT_CONTROL_REQUEST_TIMEOUT;

  return new Promise<void>((resolve) => {
    const xhr = new XMLHttpRequest();

    try {
      xhr.open('GET', fullUrl, true);
      xhr.timeout = timeout;
    } catch {
      savePingResultWithLog(
        urlFallback,
        errorResponseOrder(),
        baseUrl,
        false,
        'open-exception',
        null,
        fullUrl,
      );
      resolve();
      return;
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        savePingResultWithLog(
          urlFallback,
          responseOrder(),
          baseUrl,
          true,
          'load',
          xhr.status,
          fullUrl,
        );
      } else {
        savePingResultWithLog(
          urlFallback,
          errorResponseOrder(),
          baseUrl,
          false,
          'load_with_error',
          xhr.status,
          fullUrl,
        );
      }
      resolve();
    };
    xhr.onerror = () => {
      savePingResultWithLog(
        urlFallback,
        errorResponseOrder(),
        baseUrl,
        false,
        'error',
        null,
        fullUrl,
      );
      resolve();
    };
    xhr.ontimeout = () => {
      savePingResultWithLog(
        urlFallback,
        errorResponseOrder(),
        baseUrl,
        false,
        'timeout',
        null,
        fullUrl,
      );
      resolve();
    };
    xhr.onabort = () => {
      savePingResultWithLog(
        urlFallback,
        errorResponseOrder(),
        baseUrl,
        false,
        'abort',
        null,
        fullUrl,
      );
      resolve();
    };
    try {
      xhr.send();
    } catch {
      savePingResultWithLog(
        urlFallback,
        errorResponseOrder(),
        baseUrl,
        false,
        'send-exception',
        null,
        fullUrl,
      );
      resolve();
    }
  });
}

function savePingResultWithLog(
  urlFallback: UrlFallback,
  index: number,
  baseUrl: string,
  isSuccess: boolean,
  event: string,
  status: number | null = null,
  fullUrl: string | null = null,
): void {
  urlFallback.savePingResult(index, baseUrl, isSuccess);
  logPingRequest('save-result', {
    event,
    status,
    index,
    baseUrl,
    fullUrl,
    isSuccess,
  });
}

function isPingRequestDebugEnabled(): boolean {
  try {
    return localStorage.getItem(DEBUG_PING_REQUEST_KEY) === '1';
  } catch {
    return false;
  }
}

function logPingRequest(event: string, payload: Record<string, unknown>): void {
  if (!isPingRequestDebugEnabled()) {
    return;
  }

  console.info('[PingRequest]', event, payload);
  console.info(
    '[PingRequestPlaneJson]',
    stringifyPingRequestLog({ event, payload }),
  );
}

function stringifyPingRequestLog(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch (error) {
    return JSON.stringify({
      stringifyError: error instanceof Error ? error.message : String(error),
    });
  }
}

function normalizeBaseUrl(url: string): string {
  return url.trim().replace(/\/+$/, '');
}

function joinBaseAndPath(base: string, path: string): string {
  const relativePath = path.replace(/^\/+/, '');
  return relativePath ? `${base}/${relativePath}` : base;
}

function getNetworkConnection(): EventTarget | null {
  const nav = navigator as NavigatorWithConnection;
  return nav.connection ?? nav.mozConnection ?? nav.webkitConnection ?? null;
}
